/**
 * Recovery from a stale lazy-loaded chunk after a deploy.
 *
 * Routes are code-split with `lazy()`, and each release ships assets under new content hashes.
 * The production deploy stages `releases/<sha>/` and prunes all but the newest 5, so a tab left
 * open on an older build asks for a chunk filename that no longer exists the moment the user
 * navigates. Vite raises `vite:preloadError` on window for exactly this, and the user sees a dead
 * route. Two such issues sit in Sentry — `/login` and `/signup`, latest event 2026-07-27 09:39 on
 * release 03f3841, a build from 2026-07-15.
 *
 * Reloading pulls the current index.html and its live chunk names. The sessionStorage guard means
 * a preload failure that is NOT stale-deploy (offline, a blocked request, a genuinely broken
 * asset) reloads at most once per tab instead of looping forever.
 */
export const STALE_CHUNK_RELOAD_KEY = 'pairpen:stale-chunk-reloaded';

interface StaleChunkDeps {
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  reload: () => void;
}

/** Returns true when it triggered a reload, false when the guard suppressed it. */
export function handleStaleChunk({ storage, reload }: StaleChunkDeps): boolean {
  if (storage.getItem(STALE_CHUNK_RELOAD_KEY) === '1') {
    return false;
  }

  storage.setItem(STALE_CHUNK_RELOAD_KEY, '1');
  reload();
  return true;
}

/**
 * Clears the guard once the app has booted, so a later deploy in the same tab can recover again.
 * Called after React renders — reaching that point means the entry chunks loaded fine.
 */
export function clearStaleChunkGuard(storage: Pick<Storage, 'removeItem'>): void {
  storage.removeItem(STALE_CHUNK_RELOAD_KEY);
}

export function registerStaleChunkReload(): void {
  window.addEventListener('vite:preloadError', () => {
    handleStaleChunk({ storage: sessionStorage, reload: () => window.location.reload() });
  });
}
